import { useEffect, useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import { Alert, Button, Checkbox, Form, Input } from 'antd';
import type { Rule } from 'antd/es/form';
import {
  CheckCircleFilled,
  LoadingOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  TikTokOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AUTH_FORM_PROPS,
  AuthNote,
  ON_SUBMIT,
  TIKTOK_ID_PATTERN,
  VALIDATION,
  authStyles,
  emailRules,
  normalizeEmail,
  passwordIconRender,
  setFieldQuietly,
} from 'layouts/auth';
import { mapRegisterError, registerAccount, REGISTER_MESSAGES } from './register-api';
import type { RegisterPayload } from './register-api';
import { EmailTakenAlert, isWideScreen, revalidateIfInvalid } from './register-parts';
import RulesModal from './RulesModal';
import { useTiktokCheck } from './use-tiktok-check';
import styles from './register.module.scss';

export interface SignUpValues {
  email: string;
  phoneNumber: string;
  nickName: string;
  password: string;
  username: string;
  agree: boolean;
}

interface SignUpFormProps {
  /** Quay lại từ bước nhập mã ("Sửa thông tin"): điền lại đúng những gì đã gửi. */
  initialValues?: Partial<SignUpValues>;
  /** Backend đã nhận đăng ký và gửi mã. `email` là email backend xác nhận. */
  onRegistered: (payload: RegisterPayload, email: string) => void;
}

type ServerError = { kind: 'emailTaken'; email: string } | { kind: 'alert'; message: string };
type Phase = 'checking' | 'sending' | null;

const PHONE_MESSAGE = 'Số điện thoại chưa đúng. Nhập 8–15 chữ số, ví dụ 0912345678.';
// Như gợi ý dưới ô TikTok ID của app. "@" gõ kèm tự bị bỏ (normalizeTiktokId).
const TIKTOK_HINT = 'Chỉ gồm chữ, số, dấu chấm và gạch dưới.';

/** TikTok ID: bỏ khoảng trắng, bỏ "@" đầu (hay bị gõ kèm), chữ thường như backend lưu. */
export const normalizeTiktokId = (value?: string) =>
  (value || '').trim().replace(/^@+/, '').toLowerCase();

/** Số điện thoại gửi đi: bỏ dấu cách / chấm / gạch ngang người dùng gõ cho dễ đọc. */
const compactPhone = (value?: string) => (value || '').trim().replace(/[\s.-]/g, '');

// Luật "bắt buộc" gắn ON_SUBMIT (xem layouts/auth/auth-form.ts): chỉ báo khi bấm Tạo tài khoản,
// không báo chỉ vì con trỏ đi ngang qua một ô trống. emailRules dùng chung đã gắn sẵn.
const phoneRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: VALIDATION.phoneRequired,
    validateTrigger: ON_SUBMIT,
  },
  {
    validator: (_, value?: string) => {
      const text = (value || '').trim();
      if (!text) return Promise.resolve();
      const digits = text.replace(/\D/g, '').length;
      // DTO chỉ đòi 2–32 ký tự; chặt hơn phía client không phá tương thích.
      const ok = /^\+?\d(?:[\s.-]?\d)*$/.test(text) && digits >= 8 && digits <= 15;
      return ok ? Promise.resolve() : Promise.reject(new Error(PHONE_MESSAGE));
    },
  },
];

const nickNameRules: Rule[] = [
  {
    required: true,
    whitespace: true,
    message: VALIDATION.nickNameRequired,
    validateTrigger: ON_SUBMIT,
  },
  {
    validator: (_, value?: string) => {
      const length = (value || '').trim().length;
      if (!length || (length >= 2 && length <= 32)) return Promise.resolve();
      return Promise.reject(new Error(VALIDATION.nickNameLength));
    },
  },
];

// Đăng ký: câu bắt buộc là "Vui lòng nhập mật khẩu." (không phải "mật khẩu mới"). 8–32 như DTO.
const passwordRules: Rule[] = [
  { required: true, message: VALIDATION.passwordRequired, validateTrigger: ON_SUBMIT },
  { min: 8, message: VALIDATION.passwordMin },
  { max: 32, message: VALIDATION.passwordMax },
];

const agreeRules: Rule[] = [
  {
    validator: (_, checked?: boolean) =>
      checked ? Promise.resolve() : Promise.reject(new Error(VALIDATION.agreeRules)),
  },
];

/** Bước 1/3: form đăng ký giống app, bỏ Phòng ban / Ca / Mã HP Media. */
const SignUpForm = ({ initialValues, onRegistered }: SignUpFormProps) => {
  const [form] = Form.useForm<SignUpValues>();
  const tiktok = useTiktokCheck();
  const [phase, setPhase] = useState<Phase>(null);
  const [serverError, setServerError] = useState<ServerError | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [autoFocus] = useState(isWideScreen);
  const alertRef = useRef<HTMLDivElement>(null);
  // Chặn gửi hai lần (Enter liên tiếp trước khi nút kịp chuyển sang loading).
  const busy = useRef(false);
  const usernameValue = Form.useWatch('username', form) || '';

  // Quay lại từ bước 2: kiểm tra lại TikTok ID đã điền để ô hiện ngay "dùng được".
  useEffect(() => {
    const initial = normalizeTiktokId(initialValues?.username);
    if (initial) tiktok.schedule(initial);
    // Chỉ chạy lúc mở form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Kết quả "đã có người dùng" về sau khi đã gõ xong → hiện lỗi ngay dưới ô, không đợi bấm gửi.
  useEffect(() => {
    const { status, value } = tiktok.state;
    if (status === 'taken' && value && value === form.getFieldValue('username')) {
      form.validateFields(['username']).catch(() => undefined);
    }
  }, [tiktok.state, form]);

  // Lỗi máy chủ nằm ở đầu form; trên điện thoại nút gửi ở cuối trang → cuộn lên cho thấy.
  useEffect(() => {
    if (serverError) alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [serverError]);

  const usernameRules: Rule[] = [
    { required: true, message: VALIDATION.tiktokRequired, validateTrigger: ON_SUBMIT },
    { pattern: TIKTOK_ID_PATTERN, message: VALIDATION.tiktokFormat },
    {
      validator: (_, value?: string) =>
        value && tiktok.isTaken(value)
          ? Promise.reject(new Error(VALIDATION.tiktokTaken))
          : Promise.resolve(),
    },
  ];

  const onValuesChange = (changed: Partial<SignUpValues>) => {
    if (serverError) setServerError(null);
    if ('username' in changed) tiktok.schedule(changed.username || '');
    revalidateIfInvalid(form, changed);
  };

  const focusUsername = () => {
    form.scrollToField('username', { block: 'center' });
    form.focusField('username');
  };

  const onFinish = async (values: SignUpValues) => {
    if (busy.current) return;
    busy.current = true;
    setServerError(null);
    const payload: RegisterPayload = {
      email: normalizeEmail(values.email),
      phoneNumber: compactPhone(values.phoneNumber),
      nickName: values.nickName.trim(),
      password: values.password,
      username: normalizeTiktokId(values.username),
    };

    // Lượt kiểm tra TikTok ID khi gõ có thể chưa xong (hoặc lỗi mạng) → chờ một lần cho chắc.
    setPhase('checking');
    const availability = await tiktok.ensure(payload.username);
    if (availability === 'taken') {
      busy.current = false;
      setPhase(null);
      await form.validateFields(['username']).catch(() => undefined);
      focusUsername();
      return;
    }

    setPhase('sending');
    try {
      const email = await registerAccount(payload);
      onRegistered(payload, email);
    } catch (error) {
      busy.current = false;
      setPhase(null);
      const result = mapRegisterError(error);
      if (result.kind === 'field') {
        if (result.message === REGISTER_MESSAGES.tiktokTaken) {
          tiktok.markTaken(payload.username);
          await form.validateFields(['username']).catch(() => undefined);
        } else {
          form.setFields([{ name: 'username', errors: [result.message] }]);
        }
        focusUsername();
      } else if (result.kind === 'emailTaken') {
        setServerError({ kind: 'emailTaken', email: payload.email });
      } else {
        setServerError(result);
      }
    }
  };

  const openRules = (event: MouseEvent<HTMLAnchorElement>) => {
    // Link nằm trong <label> của ô đồng ý: chặn mặc định để bấm link không tick/bỏ tick ô.
    event.preventDefault();
    setRulesOpen(true);
  };

  const agreeToRules = () => {
    setFieldQuietly(form, 'agree', true);
    form.validateFields(['agree']).catch(() => undefined);
    setRulesOpen(false);
  };

  // Trạng thái kiểm tra TikTok ID chỉ tính khi đúng là giá trị đang nằm trong ô.
  const check = tiktok.state.value === usernameValue ? tiktok.state.status : 'idle';
  let tiktokNote: ReactNode = TIKTOK_HINT;
  if (check === 'checking') tiktokNote = REGISTER_MESSAGES.tiktokChecking;
  else if (check === 'available') {
    tiktokNote = <span className={styles.statusOk}>{REGISTER_MESSAGES.tiktokAvailable}</span>;
  } else if (check === 'error') tiktokNote = REGISTER_MESSAGES.tiktokCheckFailed;

  const submitLabel =
    phase === 'checking'
      ? 'Đang kiểm tra TikTok ID…'
      : phase === 'sending'
      ? 'Đang gửi mã xác nhận…'
      : 'Tạo tài khoản';

  return (
    <>
      <Form<SignUpValues>
        {...AUTH_FORM_PROPS}
        form={form}
        name="register-form"
        className={styles.form}
        initialValues={{ agree: false, ...initialValues }}
        onFinish={onFinish}
        onValuesChange={onValuesChange}
        scrollToFirstError={{ block: 'center', focus: true }}
      >
        <div ref={alertRef}>
          {serverError?.kind === 'emailTaken' && <EmailTakenAlert email={serverError.email} />}
          {serverError?.kind === 'alert' && (
            <Alert
              type="error"
              showIcon
              className={authStyles.formAlert}
              message={serverError.message}
            />
          )}
        </div>

        <Form.Item
          name="email"
          label="Email"
          rules={emailRules}
          validateFirst
          validateTrigger="onBlur"
          normalize={normalizeEmail}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="Nhập email của bạn"
            prefix={<MailOutlined aria-hidden="true" />}
            autoFocus={autoFocus && !initialValues?.email}
          />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label="Số điện thoại"
          rules={phoneRules}
          validateFirst
          validateTrigger="onBlur"
        >
          <Input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="Nhập số điện thoại của bạn"
            prefix={<PhoneOutlined aria-hidden="true" />}
          />
        </Form.Item>

        <Form.Item
          name="nickName"
          label="Biệt danh"
          rules={nickNameRules}
          validateFirst
          validateTrigger="onBlur"
          extra="Tên hiển thị trong app HP Media, 2–32 ký tự."
        >
          <Input
            autoComplete="nickname"
            placeholder="Tên hiển thị của bạn"
            prefix={<UserOutlined aria-hidden="true" />}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Mật khẩu"
          rules={passwordRules}
          validateFirst
          validateTrigger="onBlur"
          extra="8–32 ký tự. Mật khẩu này dùng cho cả trang quản trị và app HP Media."
        >
          <Input.Password
            autoComplete="new-password"
            placeholder="Tạo mật khẩu"
            prefix={<LockOutlined aria-hidden="true" />}
            iconRender={passwordIconRender}
          />
        </Form.Item>

        <Form.Item
          name="username"
          label="TikTok ID"
          rules={usernameRules}
          validateFirst
          validateTrigger="onBlur"
          normalize={normalizeTiktokId}
          extra={
            <span role="status" aria-live="polite">
              {tiktokNote}
            </span>
          }
        >
          <Input
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Nhập TikTok ID của bạn"
            prefix={<TikTokOutlined aria-hidden="true" />}
            // Luôn có phần tử suffix: thêm/bớt suffix làm antd dựng lại ô và mất con trỏ.
            suffix={
              <span className={styles.tiktokState} aria-hidden="true">
                {check === 'checking' && <LoadingOutlined />}
                {check === 'available' && <CheckCircleFilled className={styles.statusOk} />}
              </span>
            }
          />
        </Form.Item>

        <Form.Item name="agree" valuePropName="checked" rules={agreeRules} className={styles.agree}>
          <Checkbox>
            Tôi đã đọc và đồng ý với{' '}
            <a href="#quy-tac-cong-dong" className={styles.inlineLink} onClick={openRules}>
              Quy tắc cộng đồng
            </a>{' '}
            của HP Media.
          </Checkbox>
        </Form.Item>

        <Button type="primary" htmlType="submit" size="large" block loading={!!phase}>
          {submitLabel}
        </Button>

        <AuthNote>
          Chúng tôi sẽ gửi mã xác nhận 4 số tới email này. Nhập mã ở bước sau để kích hoạt tài
          khoản.
        </AuthNote>
      </Form>

      <RulesModal open={rulesOpen} onClose={() => setRulesOpen(false)} onAgree={agreeToRules} />
    </>
  );
};

export default SignUpForm;
